import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usersApi, permissionsApi } from '../../api/endpoints';
import {
  UserDto,
  AppModule,
  PermissionLevel,
  AppModuleLabels,
  PermissionLevelLabels,
} from '../../types';
import { FiRefreshCw, FiSave, FiShield, FiChevronDown, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';

const MODULES: AppModule[] = [
  'Dashboard',
  'PurchaseOrders',
  'Shipments',
  'Invoices',
  'Products',
  'Inventory',
  'Users',
  'Sync',
];

const PERMISSION_LEVELS: PermissionLevel[] = ['None', 'Read', 'Write'];

const LEVEL_COLORS: Record<string, { bg: string; color: string }> = {
  None: { bg: 'var(--gray-100)', color: 'var(--text-muted)' },
  Read: { bg: 'rgba(59, 130, 246, 0.1)', color: 'rgb(59, 130, 246)' },
  Write: { bg: 'rgba(16, 185, 129, 0.1)', color: 'rgb(16, 185, 129)' },
};

type UserPermissionsMap = Record<string, Record<string, string>>;

/* ── Inline dropdown for permission level ─────────────────────── */
const PermissionSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const LEVEL_LABELS: Record<string, string> = {
    None: t('permissions.none'),
    Read: t('permissions.read'),
    Write: t('permissions.write'),
  };
  const colorStyle = LEVEL_COLORS[value] || LEVEL_COLORS.None;
  const style = { ...colorStyle, label: LEVEL_LABELS[value] || LEVEL_LABELS.None };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 5,
          border: 'none',
          background: style.bg,
          color: style.color,
          fontWeight: 600,
          fontSize: 11,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'all 0.15s ease',
        }}
      >
        {style.label}
        <FiChevronDown
          size={12}
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      </button>
      {open && (
        <div
          style={{
            position: 'fixed',
            zIndex: 9999,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            padding: 4,
            minWidth: 160,
            marginTop: 4,
          }}
          ref={(el) => {
            if (!el || !ref.current) return;
            const btn = ref.current.getBoundingClientRect();
            el.style.top = `${btn.bottom + 4}px`;
            el.style.left = `${btn.left}px`;
          }}
        >
          {PERMISSION_LEVELS.map((level) => {
            const ls = { ...LEVEL_COLORS[level], label: LEVEL_LABELS[level] || level };
            const selected = level === value;
            return (
              <button
                key={level}
                type="button"
                onClick={() => { onChange(level); setOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: 6,
                  background: selected ? ls.bg : 'transparent',
                  color: selected ? ls.color : 'var(--text)',
                  fontWeight: selected ? 600 : 450,
                  fontSize: 13,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--gray-100)'; }}
                onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: ls.color,
                      flexShrink: 0,
                    }}
                  />
                  {PermissionLevelLabels[level as PermissionLevel]}
                </span>
                {selected && <FiCheck size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Main page ────────────────────────────────────────────────── */
const PermissionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<UserPermissionsMap>({});
  const [originalPermissions, setOriginalPermissions] = useState<UserPermissionsMap>({});
  const [savingUserId, setSavingUserId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const usersRes = await usersApi.list();
      const userList: UserDto[] = usersRes.data;
      setUsers(userList);

      const permResults = await Promise.all(
        userList.map((user) => permissionsApi.getForUser(user.id))
      );

      const permsMap: UserPermissionsMap = {};
      userList.forEach((user, index) => {
        const raw = permResults[index].data;
        const userPerms: Record<string, string> = {};
        if (Array.isArray(raw)) {
          raw.forEach((p: { module: string; level: string }) => {
            userPerms[p.module] = p.level;
          });
        } else if (raw && typeof raw === 'object') {
          Object.assign(userPerms, raw);
        }
        MODULES.forEach((mod) => {
          if (!userPerms[mod]) {
            userPerms[mod] = 'None';
          }
        });
        permsMap[user.id] = userPerms;
      });

      setPermissions(permsMap);
      setOriginalPermissions(JSON.parse(JSON.stringify(permsMap)));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('permissions.fetchError')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePermissionChange = useCallback(
    (userId: string, module: string, level: string) => {
      setPermissions((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [module]: level,
        },
      }));
    },
    []
  );

  const isRowDirty = useCallback(
    (userId: string): boolean => {
      const current = permissions[userId];
      const original = originalPermissions[userId];
      if (!current || !original) return false;
      return MODULES.some((mod) => current[mod] !== original[mod]);
    },
    [permissions, originalPermissions]
  );

  const handleSave = useCallback(
    async (userId: string) => {
      const userPerms = permissions[userId];
      if (!userPerms) return;

      setSavingUserId(userId);
      try {
        const payload: Record<string, string> = {};
        MODULES.forEach((mod) => {
          payload[mod] = userPerms[mod] || 'None';
        });
        await permissionsApi.setForUser(userId, payload);
        toast.success(t('permissions.saved'));
        setOriginalPermissions((prev) => ({
          ...prev,
          [userId]: { ...userPerms },
        }));
      } catch (error: any) {
        toast.error(
          error.response?.data?.message || t('permissions.saveError')
        );
      } finally {
        setSavingUserId(null);
      }
    },
    [permissions]
  );

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('permissions.title')}</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={fetchData}>
            <FiRefreshCw /> {t('common.refresh')}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3><FiShield style={{ marginRight: 8, verticalAlign: -2 }} />{t('permissions.matrix')}</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 160, padding: '10px 14px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('permissions.user')}
                  </th>
                  {MODULES.map((mod) => (
                    <th key={mod} style={{ padding: '10px 8px', textAlign: 'center', whiteSpace: 'nowrap', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {AppModuleLabels[mod]}
                    </th>
                  ))}
                  <th style={{ padding: '10px 8px', textAlign: 'center', width: 80, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const dirty = isRowDirty(user.id);
                  const saving = savingUserId === user.id;
                  return (
                    <tr
                      key={user.id}
                      style={{
                        background: dirty ? 'rgba(239, 68, 68, 0.04)' : undefined,
                        transition: 'background 0.2s ease',
                      }}
                    >
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{user.displayName}</div>
                        <div style={{ fontSize: 11, opacity: 0.5, marginTop: 1 }}>{user.email}</div>
                      </td>
                      {MODULES.map((mod) => {
                        const currentLevel = permissions[user.id]?.[mod] || 'None';
                        return (
                          <td key={mod} style={{ textAlign: 'center', padding: '6px 4px' }}>
                            <PermissionSelect
                              value={currentLevel}
                              onChange={(value) => handlePermissionChange(user.id, mod, value)}
                            />
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {dirty && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSave(user.id)}
                            disabled={saving}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {saving ? (
                              '...'
                            ) : (
                              <>
                                <FiSave /> {t('common.save')}
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>
              {t('common.noUsers')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
